import { useState, useMemo, useCallback, type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HelpSupportButton } from '@/components/help-support';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { DataTable, type DataTableColumn, type DataTablePagination } from '@/components/shared/data-table';
import { Banknote, TrendingUp, AlertTriangle, Calendar, FileSpreadsheet, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import {
  useCompanyPerformanceList,
  useCompanyTrendData,
  type CompanyPerformance,
  type CompanyTrendMetrics,
} from '@/hooks/useCompanyPerformanceGraphQL';

const DEFAULT_PAGE_SIZE = 10;

type TrendPeriod = 'latest' | '7days' | '30days' | '60days' | '90days';

function periodToDays(period: TrendPeriod): number {
  switch (period) {
    case '7days':
      return 7;
    case '30days':
      return 30;
    case '60days':
      return 60;
    case '90days':
      return 90;
    default:
      return 0;
  }
}

function getTrendColorClass(neutral: boolean, improved: boolean): string {
  if (neutral) return 'text-muted-foreground';
  if (improved) return 'text-green-600';
  return 'text-red-600';
}

function formatSignedValue(value: number): string {
  if (value > 0) return '+';
  return '';
}

export default function MultiCompanyDashboard() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState<TrendPeriod>('latest');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('all');
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<
    string | null,
    'asc' | 'desc'
  >('multi-company-dashboard', null, 'asc');

  const { data: companyPerformanceData, isLoading } = useCompanyPerformanceList(selectedCurrency);

  const trendPeriodDays = periodToDays(viewMode);
  const { data: trendDataMap, isLoading: isTrendLoading } = useCompanyTrendData(trendPeriodDays);
  const isTrendMode = viewMode !== 'latest';

  // Always fetch a fixed 30-day trend to power the 'AVG DSO (LAST 30 DAYS)' card,
  // regardless of the selected table view period.
  const { data: dsoCardTrendMap } = useCompanyTrendData(30);

  const sourceData = useMemo<CompanyPerformance[]>(() => companyPerformanceData ?? [], [companyPerformanceData]);

  const availableCurrencies = useMemo(() => {
    const currencies = Array.from(new Set(sourceData.map((x) => x.currency).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b),
    );
    return currencies;
  }, [sourceData]);

  const currencyFilteredData = useMemo(() => {
    if (selectedCurrency === 'all') {
      return sourceData;
    }
    return sourceData.filter((x) => x.currency === selectedCurrency);
  }, [sourceData, selectedCurrency]);

  const totalOutstanding = currencyFilteredData.reduce((sum, x) => sum + x.totalAR, 0);
  const totalOverdue = currencyFilteredData.reduce((sum, x) => sum + x.overdue, 0);
  const avgDSO =
    currencyFilteredData.length > 0
      ? Math.round(currencyFilteredData.reduce((sum, x) => sum + x.dso, 0) / currencyFilteredData.length)
      : 0;

  const mostAtRiskCompany =
    currencyFilteredData.length > 0
      ? currencyFilteredData.reduce(
          (max, x) => (x.overduePercent > max.overduePercent ? x : max),
          currencyFilteredData[0],
        )
      : null;

  // Aggregate real DSO change across the filtered companies from the 30-day trend.
  // dsoChange = latestDso - earliestDso; negative = DSO fell = improvement.
  const avgDsoChange = useMemo<number | null>(() => {
    if (!dsoCardTrendMap || currencyFilteredData.length === 0) return null;
    const changes = currencyFilteredData
      .map((x) => dsoCardTrendMap.get(x.id)?.dsoChange)
      .filter((v): v is number => v != null);
    if (changes.length === 0) return null;
    return Math.round(changes.reduce((sum, v) => sum + v, 0) / changes.length);
  }, [dsoCardTrendMap, currencyFilteredData]);

  const avgDsoSecondaryValue =
    avgDsoChange == null
      ? undefined
      : avgDsoChange === 0
        ? 'No change vs 30 days ago'
        : avgDsoChange < 0
          ? `${Math.abs(avgDsoChange)} days improvement`
          : `+${avgDsoChange} days vs 30 days ago`;

  const overduePercentOfOutstanding =
    totalOutstanding > 0 ? `${((totalOverdue / totalOutstanding) * 100).toFixed(1)}% is overdue` : undefined;

  const overdueCompanyCount = currencyFilteredData.filter((x) => x.overdue > 0).length;
  const totalCompanyCount = currencyFilteredData.length;
  const overdueCompanySpread =
    totalCompanyCount > 0
      ? `Across ${overdueCompanyCount} of ${totalCompanyCount} ${totalCompanyCount === 1 ? 'company' : 'companies'}`
      : undefined;

  const companyOptions = sourceData.map((x) => ({
    value: x.id,
    label: x.name,
  }));

  const filteredData = useMemo(() => {
    let data = currencyFilteredData;

    if (selectedCompanyIds.length > 0) {
      data = data.filter((x) => selectedCompanyIds.includes(x.id));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((x) => x.name.toLowerCase().includes(query));
    }

    return data;
  }, [currencyFilteredData, selectedCompanyIds, searchQuery]);

  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField as keyof CompanyPerformance];
      const bVal = b[sortField as keyof CompanyPerformance];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp: number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal);
      } else {
        cmp = Number(aVal) - Number(bVal);
      }

      return sortDirection === 'desc' ? -cmp : cmp;
    });
  }, [filteredData, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => setCurrentPage(1), [selectedCurrency, selectedCompanyIds, searchQuery]);

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSort(field, 'asc');
      }
    },
    [sortField, sortDirection, setSort],
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const formatCurrency = (value: number, currency?: string) => {
    const currencyCode = currency || (selectedCurrency === 'all' ? 'GBP' : selectedCurrency);
    const locale = currencyCode === 'USD' ? 'en-US' : 'en-GB';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getOverdueColor = (percent: number) => {
    if (percent >= 30) return 'text-red-600';
    if (percent >= 20) return 'text-orange-600';
    return 'text-green-600';
  };

  const getDSOColor = (dso: number) => {
    if (dso >= 60) return 'text-red-600';
    if (dso >= 45) return 'text-orange-600';
    return 'text-green-600';
  };

  const getCEIColor = (cei: number) => {
    if (cei >= 90) return 'text-green-600';
    if (cei >= 75) return 'text-orange-600';
    return 'text-red-600';
  };

  const latestColumns: DataTableColumn[] = [
    { id: 'name', label: 'Company', sortKey: 'name', align: 'left', defaultWidth: 200, truncate: 'whenSized' },
    { id: 'currency', label: 'Currency', sortKey: 'currency', align: 'left', defaultWidth: 100 },
    { id: 'totalAR', label: 'Total AR', sortKey: 'totalAR', align: 'right', defaultWidth: 140 },
    { id: 'overdue', label: 'Overdue', sortKey: 'overdue', align: 'right', defaultWidth: 140 },
    { id: 'overduePercent', label: 'Overdue %', sortKey: 'overduePercent', align: 'right', defaultWidth: 110 },
    { id: 'dso', label: 'DSO', sortKey: 'dso', align: 'right', defaultWidth: 80 },
    { id: 'cei', label: 'CEI', sortKey: 'cei', align: 'right', defaultWidth: 80 },
    { id: 'avgDaysToPay', label: 'Avg Days to Pay', sortKey: 'avgDaysToPay', align: 'right', defaultWidth: 140 },
  ];

  const trendColumns: DataTableColumn[] = [
    { id: 'name', label: 'Company', sortKey: 'name', align: 'left', defaultWidth: 200, truncate: 'whenSized' },
    { id: 'currency', label: 'Currency', sortKey: 'currency', align: 'left', defaultWidth: 100 },
    { id: 'totalAR', label: 'Total AR', sortKey: 'totalAR', align: 'right', defaultWidth: 140 },
    { id: 'overdue', label: 'Overdue', sortKey: 'overdue', align: 'right', defaultWidth: 140 },
    { id: 'dsoTrend', label: 'DSO Trend', align: 'right', defaultWidth: 120 },
    { id: 'ceiTrend', label: 'CEI Trend', align: 'right', defaultWidth: 120 },
  ];

  const columns = viewMode === 'latest' ? latestColumns : trendColumns;

  const getTrendMetrics = (companyId: string): CompanyTrendMetrics | undefined => {
    return trendDataMap?.get(companyId);
  };

  const renderCell = useCallback(
    (columnId: string, item: CompanyPerformance): ReactNode => {
      switch (columnId) {
        case 'name':
          return (
            <button
              type="button"
              className="text-primary hover:underline cursor-pointer font-medium text-left"
              onClick={() => navigate(`/analytics-reporting/multi-company/company/${item.id}`)}
            >
              {item.name}
            </button>
          );
        case 'currency':
          return (
            <Badge variant="outline" className="font-mono">
              {item.currency}
            </Badge>
          );
        case 'totalAR':
          return <span className="text-right">{formatCurrency(item.totalAR, item.currency)}</span>;
        case 'overdue':
          return <span className="text-right">{formatCurrency(item.overdue, item.currency)}</span>;
        case 'overduePercent':
          return (
            <span className={cn('font-medium', getOverdueColor(item.overduePercent))}>
              {item.overduePercent.toFixed(1)}%
            </span>
          );
        case 'dso':
          return <span className={cn('font-medium', getDSOColor(item.dso))}>{item.dso}</span>;
        case 'cei':
          return <span className={cn('font-medium', getCEIColor(item.cei))}>{item.cei}%</span>;
        case 'avgDaysToPay':
          return <span>{item.avgDaysToPay}</span>;
        case 'dsoTrend':
        case 'ceiTrend':
          return renderTrendCell(columnId, item);
        default:
          return null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [navigate, selectedCurrency, trendDataMap],
  );

  const renderTrendCell = (columnId: string, item: CompanyPerformance): ReactNode => {
    const metrics = getTrendMetrics(item.id);
    const emptyCell = <span className="text-muted-foreground">{'\u2014'}</span>;

    if (columnId === 'dsoTrend') {
      if (metrics?.dsoChange == null) return emptyCell;
      const colorClass = getTrendColorClass(metrics.dsoChange === 0, metrics.dsoChange < 0);
      const sign = formatSignedValue(metrics.dsoChange);
      return (
        <span className={cn('font-medium', colorClass)}>
          {sign}
          {metrics.dsoChange} days
        </span>
      );
    }

    if (metrics?.ceiChange == null) return emptyCell;
    const colorClass = getTrendColorClass(metrics.ceiChange === 0, metrics.ceiChange > 0);
    const sign = formatSignedValue(metrics.ceiChange);
    return (
      <span className={cn('font-medium', colorClass)}>
        {sign}
        {metrics.ceiChange.toFixed(1)}%
      </span>
    );
  };

  const getExportData = () =>
    filteredData.map((x) => {
      const metrics = getTrendMetrics(x.id);
      const dsoSign = metrics?.dsoChange != null ? formatSignedValue(metrics.dsoChange) : '';
      const dsoTrendStr = metrics?.dsoChange != null ? `${dsoSign}${metrics.dsoChange} days` : '\u2014';
      const ceiSign = metrics?.ceiChange != null ? formatSignedValue(metrics.ceiChange) : '';
      const ceiTrendStr = metrics?.ceiChange != null ? `${ceiSign}${metrics.ceiChange.toFixed(1)}%` : '\u2014';
      const baseRow = {
        Company: x.name,
        Currency: x.currency,
        'Total AR': formatCurrency(x.totalAR, x.currency),
        Overdue: formatCurrency(x.overdue, x.currency),
        'Overdue %': `${x.overduePercent.toFixed(1)}%`,
        DSO: x.dso,
        CEI: `${x.cei}%`,
        'Avg Days to Pay': x.avgDaysToPay,
      };
      if (isTrendMode) {
        return {
          ...baseRow,
          'DSO Trend': dsoTrendStr,
          'CEI Trend': ceiTrendStr,
        };
      }
      return baseRow;
    });

  const exportToCSV = () => {
    const dataToExport = getExportData();
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map((row) =>
        headers
          .map((header) => {
            const value = row[header as keyof typeof row];
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `company-performance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pagination: DataTablePagination = {
    currentPage,
    totalPages,
    pageSize,
    totalItems: sortedData.length,
    onPageChange: setCurrentPage,
    onPageSizeChange: handlePageSizeChange,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Multi-Company Dashboard</h1>
          <p className="text-muted-foreground">Consolidated view across all companies in your portfolio</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Currencies</SelectItem>
              {availableCurrencies.map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <HelpSupportButton screenId="multi-company-dashboard" />
        </div>
      </div>

      {/* Top Section - Key Performance Metrics */}
      {selectedCurrency === 'all' ? (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-amber-900">Multi-Currency View</p>
              <p className="text-sm text-amber-800">
                Companies use different currencies. Please select a specific currency to view aggregated metrics, or use
                the table below to compare individual company performance.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <NavigationMetricCardAlt
            title="TOTAL OUTSTANDING"
            value={formatCurrency(totalOutstanding)}
            secondaryValue={overduePercentOfOutstanding}
            icon={Banknote}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />

          <NavigationMetricCardAlt
            title="TOTAL OVERDUE"
            value={formatCurrency(totalOverdue)}
            secondaryValue={overdueCompanySpread}
            icon={TrendingUp}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />

          <NavigationMetricCardAlt
            title="MOST AT-RISK COMPANY"
            value={mostAtRiskCompany?.name || 'N/A'}
            secondaryValue={
              mostAtRiskCompany ? `${mostAtRiskCompany.overduePercent.toFixed(1)}% overdue` : 'No companies'
            }
            icon={AlertTriangle}
            variant="info"
            onClick={() =>
              mostAtRiskCompany && navigate(`/analytics-reporting/multi-company/company/${mostAtRiskCompany.id}`)
            }
            showButton={!!mostAtRiskCompany}
          />

          <NavigationMetricCardAlt
            title="AVG DSO (LAST 30 DAYS)"
            value={`${avgDSO} days`}
            secondaryValue={avgDsoSecondaryValue}
            icon={Calendar}
            variant="info"
            onClick={() => {}}
            showButton={false}
          />
        </div>
      )}

      {/* Bottom Section - Company Performance Comparison Table */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Table Header with Search and Filters */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold whitespace-nowrap">Company Performance Comparison</h2>
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-1 max-w-xs">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <MultiSelect
                  options={companyOptions}
                  placeholder="Filter by companies..."
                  emptyMessage="No companies found"
                  value={selectedCompanyIds}
                  onValueChange={setSelectedCompanyIds}
                  maxDisplay={2}
                />
              </div>
              <Select value={viewMode} onValueChange={(v) => setViewMode(v as TrendPeriod)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest</SelectItem>
                  <SelectItem value="7days">7 Days</SelectItem>
                  <SelectItem value="30days">30 Days</SelectItem>
                  <SelectItem value="60days">60 Days</SelectItem>
                  <SelectItem value="90days">90 Days</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={filteredData.length === 0}
                    title="Export"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToCSV}>Export Current View</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* DataTable */}
          <DataTable<CompanyPerformance>
            columns={columns}
            data={paginatedData}
            getRowId={(item) => item.id}
            renderCell={renderCell}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isLoading={isLoading || (isTrendMode && isTrendLoading)}
            emptyContent={<p className="text-sm text-muted-foreground">No companies found matching your filters</p>}
            pagination={pagination}
          />
        </div>
      </Card>
    </div>
  );
}
